import React, { useMemo, useEffect } from 'react';
import DOMPurify from 'dompurify';
import './rich-text-editor.css';

const HTML_TAG_RE = /<\/?[a-z][\s\S]*>/i;

const YT_HOSTS = new Set([
  'www.youtube.com', 'youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com',
]);

// Allow only YouTube iframes; remove any other iframe sources (prevents XSS / unwanted embeds).
function configureDomPurify() {
  if (typeof window === 'undefined') return;
  if (configureDomPurify._done) return;
  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName !== 'iframe') return;
    try {
      const src = node.getAttribute('src') || '';
      const u = new URL(src, window.location.origin);
      if (!YT_HOSTS.has(u.hostname.toLowerCase())) {
        node.parentNode && node.parentNode.removeChild(node);
      }
    } catch {
      node.parentNode && node.parentNode.removeChild(node);
    }
  });
  configureDomPurify._done = true;
}

/**
 * Safely render rich text produced by RichTextEditor.
 *
 * - If content contains HTML, sanitize with DOMPurify and inject.
 * - If plain text (legacy content without HTML), render as paragraphs preserving line breaks.
 * - Returns null when there is nothing to show.
 */
export default function RichContent({ html, className = '', testid, clamp }) {
  useEffect(() => { configureDomPurify(); }, []);
  const sanitized = useMemo(() => {
    configureDomPurify();
    const value = (html || '').toString();
    if (!value.trim()) return null;
    if (!HTML_TAG_RE.test(value)) {
      // Plain text → preserve newlines
      return { __html: DOMPurify.sanitize(value.replace(/\n/g, '<br />')) };
    }
    return {
      __html: DOMPurify.sanitize(value, {
        ALLOWED_TAGS: [
          'p', 'br', 'strong', 'em', 'u', 's', 'ul', 'ol', 'li', 'a',
          'blockquote', 'code', 'pre', 'h2', 'h3', 'span',
          'img', 'iframe',
        ],
        ALLOWED_ATTR: [
          'href', 'target', 'rel', 'class', 'style',
          'src', 'alt', 'title', 'width', 'height',
          'frameborder', 'allow', 'allowfullscreen', 'loading',
        ],
        ADD_ATTR: ['target', 'allowfullscreen'],
        ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
      }),
    };
  }, [html]);

  if (!sanitized) return null;
  return (
    <div
      className={`rich-content ${clamp ? `line-clamp-${clamp}` : ''} ${className}`}
      data-testid={testid}
      dangerouslySetInnerHTML={sanitized}
    />
  );
}

/** Strip HTML tags, collapse whitespace — handy for meta descriptions / list previews. */
export function stripHtml(html, maxLen) {
  if (!html) return '';
  const text = String(html).replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  if (maxLen && text.length > maxLen) return text.slice(0, maxLen - 1) + '…';
  return text;
}
